const ENDPOINT = '/api/client-error'
const MAX_MESSAGE = 1_000
const MAX_STACK = 8_000
const MAX_EXTRA = 4_000
const MAX_BREADCRUMBS = 20
const MAX_BREADCRUMB = 200
const MAX_PROBLEM_REPORTS_PER_WINDOW = 20
const MAX_INFO_REPORTS_PER_WINDOW = 10
const RATE_WINDOW_MS = 60_000
const REPEAT_WINDOW_MS = 30_000
const MAX_REMEMBERED_REPORTS = 200
const PINO_LEVELS = { info: 30, warn: 40, error: 50 } as const

export type ClientErrorLevel = 'error' | 'warn' | 'info'

export type ClientErrorPayload = {
  level: ClientErrorLevel
  name: string
  message: string
  stack?: string
  path: string
  tags?: Record<string, string>
  extra?: string
  breadcrumbs?: string[]
}

export type ClientErrorOptions = {
  level?: ClientErrorLevel
  tags?: Record<string, string>
  extra?: unknown
}

const recentReports = new Map<string, number>()
const breadcrumbs: string[] = []
let windowStart = 0
let problemReportsInWindow = 0
let infoReportsInWindow = 0

/** Drops the query string and hash, which can carry tokens or patient details. */
export const stripQuery = (url: string) => url.split(/[?#]/)[0]

const ID_SEGMENT = /^(\d{6,}|(?=[^/]*\d)[A-Za-z0-9_-]{12,}|[^/]*@[^/]*)$/

/** Replaces path segments that look like identifiers (long numbers, long mixed IDs, emails) with ":id". */
export const maskPathIds = (path: string) =>
  path
    .split('/')
    .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')

const truncate = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value)

const serializeExtra = (extra: unknown): string | undefined => {
  if (extra === undefined) return undefined
  try {
    return truncate(JSON.stringify(extra), MAX_EXTRA)
  } catch {
    return undefined
  }
}

const currentBreadcrumbs = () => (breadcrumbs.length > 0 ? [...breadcrumbs] : undefined)

/** Builds the report for an error. Values that are not Error instances are described by type only, since they are often API response bodies. */
export const buildClientErrorPayload = (error: unknown, { level = 'error', tags, extra }: ClientErrorOptions, pathname: string): ClientErrorPayload => {
  const isError = error instanceof Error
  return {
    level,
    name: isError ? error.name : 'NonErrorThrown',
    message: isError ? truncate(error.message, MAX_MESSAGE) : `Non-Error value thrown (${error === null ? 'null' : typeof error})`,
    stack: isError && error.stack ? truncate(error.stack, MAX_STACK) : undefined,
    path: stripQuery(pathname),
    tags,
    extra: serializeExtra(extra),
    breadcrumbs: currentBreadcrumbs()
  }
}

/** Builds the report for a message that has no Error behind it. */
export const buildClientMessagePayload = (message: string, { level = 'info', tags, extra }: ClientErrorOptions, pathname: string): ClientErrorPayload => ({
  level,
  name: 'Message',
  message: truncate(message, MAX_MESSAGE),
  path: stripQuery(pathname),
  tags,
  extra: serializeExtra(extra),
  breadcrumbs: currentBreadcrumbs()
})

/** Writes the report as one JSON log line, which the Vercel log drain ships to Grafana like pino output. */
const logOnServer = ({ level, name, message, stack, tags, extra }: ClientErrorPayload) => {
  console.log(
    JSON.stringify({
      level: PINO_LEVELS[level],
      time: new Date().toISOString(),
      source: 'server',
      clientError: { name, stack, tags, extra },
      msg: message
    })
  )
}

const isRateLimited = (level: ClientErrorLevel, now: number) => {
  if (now - windowStart >= RATE_WINDOW_MS) {
    windowStart = now
    problemReportsInWindow = 0
    infoReportsInWindow = 0
  }
  return level === 'info' ? infoReportsInWindow >= MAX_INFO_REPORTS_PER_WINDOW : problemReportsInWindow >= MAX_PROBLEM_REPORTS_PER_WINDOW
}

/** Skips a report identical to one sent in the last 30 seconds, including its tags and extra, and counts the ones that go out. */
const isFreshReport = (payload: ClientErrorPayload, now: number) => {
  const key = [payload.level, payload.name, payload.message, payload.stack?.split('\n')[1] ?? '', JSON.stringify(payload.tags ?? {}), payload.extra ?? ''].join('|')
  const lastSent = recentReports.get(key)
  if (lastSent !== undefined && now - lastSent < REPEAT_WINDOW_MS) return false

  if (recentReports.size >= MAX_REMEMBERED_REPORTS) {
    for (const [reportKey, sentAt] of recentReports) {
      if (now - sentAt >= REPEAT_WINDOW_MS) recentReports.delete(reportKey)
    }
    // A burst of distinct reports can leave every entry recent, so evict the oldest to stay bounded
    for (const reportKey of recentReports.keys()) {
      if (recentReports.size < MAX_REMEMBERED_REPORTS) break
      recentReports.delete(reportKey)
    }
  }
  // Delete first so a re-sent report moves to the newest position and is evicted last
  recentReports.delete(key)
  recentReports.set(key, now)
  if (payload.level === 'info') infoReportsInWindow++
  else problemReportsInWindow++
  return true
}

const postToEndpoint = (payload: ClientErrorPayload) => {
  const body = JSON.stringify(payload)
  try {
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) return
  } catch {}
  fetch(ENDPOINT, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {})
}

/** Browser reports are capped per minute and repeats are skipped. Server reports skip repeats too, but have no cap, since one server instance handles many users. */
const report = (buildPayload: () => ClientErrorPayload, level: ClientErrorLevel) => {
  const isServer = typeof window === 'undefined'
  const now = Date.now()
  if (!isServer && isRateLimited(level, now)) return

  const payload = buildPayload()
  if (!isFreshReport(payload, now)) return
  if (isServer) logOnServer(payload)
  else postToEndpoint(payload)
}

/** Reports an error to Grafana: from the browser through Connect's logging endpoint, on the server as a JSON log line. */
export const reportClientError = (error: unknown, options: ClientErrorOptions = {}) => {
  report(() => buildClientErrorPayload(error, options, typeof window === 'undefined' ? '' : window.location.pathname), options.level ?? 'error')
}

/** Reports a message, such as a missing-data warning, the same way as reportClientError. Defaults to info level. */
export const reportClientMessage = (message: string, options: ClientErrorOptions = {}) => {
  report(() => buildClientMessagePayload(message, options, typeof window === 'undefined' ? '' : window.location.pathname), options.level ?? 'info')
}

/** Records a user step, such as a login or cart change, to send with later browser reports. Only a category and short message are kept, never data objects. */
export const addClientBreadcrumb = (category: string, message: string) => {
  if (typeof window === 'undefined') return
  breadcrumbs.push(truncate(`${new Date().toISOString().slice(11, 19)} ${category}: ${message}`, MAX_BREADCRUMB))
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift()
}
